(function () {
  'use strict';

  if (window.vtcEventVideographyInitialized) return;
  window.vtcEventVideographyInitialized = true;

  var activeModal = null;
  var activeTrigger = null;
  var scrollLock = null;
  var suspendedHeroVideos = [];
  var lastInteractionWasKeyboard = false;
  var preloadObserver = null;
  var preloadQueue = [];
  var activePreloads = 0;
  var maxConcurrentPreloads = 2;
  var maxPreparedVideos = 10;
  var backgroundPreloadsPaused = false;

  function getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll('button:not([disabled]), video[controls], input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')
    ).filter(function (element) {
      return !element.hidden && element.offsetParent !== null;
    });
  }

  function lockPageScroll() {
    if (scrollLock) return;

    var body = document.body;
    var documentElement = document.documentElement;
    var scrollbarWidth = Math.max(0, window.innerWidth - documentElement.clientWidth);
    var bodyPaddingRight = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
    scrollLock = {
      y: window.scrollY,
      documentOverflow: documentElement.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPaddingRight: body.style.paddingRight
    };

    body.classList.add('evp-video-open');
    documentElement.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    if (scrollbarWidth) body.style.paddingRight = bodyPaddingRight + scrollbarWidth + 'px';
  }

  function unlockPageScroll() {
    if (!scrollLock) return;

    var body = document.body;
    var documentElement = document.documentElement;
    var y = scrollLock.y;
    body.classList.remove('evp-video-open');
    documentElement.style.overflow = scrollLock.documentOverflow;
    body.style.overflow = scrollLock.bodyOverflow;
    body.style.paddingRight = scrollLock.bodyPaddingRight;
    scrollLock = null;
    window.scrollTo(0, y);
  }

  function mountModalInViewport(modal) {
    if (!modal || modal.parentNode === document.body) return;

    modal._evpOriginParent = modal.parentNode;
    modal._evpOriginNextSibling = modal.nextSibling;
    document.body.appendChild(modal);
  }

  function restoreModalOrigin(modal) {
    if (!modal || !modal._evpOriginParent) return;

    var parent = modal._evpOriginParent;
    var nextSibling = modal._evpOriginNextSibling;
    modal._evpOriginParent = null;
    modal._evpOriginNextSibling = null;

    if (!parent.isConnected) {
      modal.remove();
      return;
    }

    if (nextSibling && nextSibling.parentNode === parent) parent.insertBefore(modal, nextSibling);
    else parent.appendChild(modal);
  }

  function suspendHeroPreviews() {
    suspendedHeroVideos = [];
    document.querySelectorAll('.evp-hero__preview-video').forEach(function (video) {
      if (video.paused || video.ended) return;
      suspendedHeroVideos.push(video);
      try {
        video.pause();
      } catch (error) {}
    });
  }

  function resumeHeroPreviews() {
    var videos = suspendedHeroVideos.slice();
    suspendedHeroVideos = [];

    videos.forEach(function (video) {
      if (!video.isConnected) return;
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
    });
  }

  function stopAndClearVideo(video) {
    if (!video) return;

    try {
      video.pause();
    } catch (error) {}

    video.removeAttribute('src');
    video.querySelectorAll('source').forEach(function (source) {
      source.removeAttribute('src');
    });

    try {
      video.load();
    } catch (error) {}

    video.remove();
  }

  function releaseVideoHost(host) {
    if (!host) return;

    var player = host.querySelector('.evp-player');
    if (player && typeof player._evpDestroy === 'function') player._evpDestroy();

    var video = host.querySelector('video');
    stopAndClearVideo(video);
    host.replaceChildren();
  }

  function getPreloadPool(root) {
    if (!root) return null;

    var pool = root.querySelector('[data-evp-preload-pool]');
    if (pool) return pool;

    pool = document.createElement('div');
    pool.className = 'evp-video-preload-pool';
    pool.dataset.evpPreloadPool = '';
    pool.setAttribute('aria-hidden', 'true');
    root.appendChild(pool);
    return pool;
  }

  function cancelPreparedVideo(trigger) {
    if (!trigger) return;

    var video = trigger._evpPreparedVideo;
    if (!video) return;

    if (typeof video._evpCancelPreload === 'function') {
      video._evpCancelPreload();
      return;
    }

    trigger._evpPreparedVideo = null;
    trigger._evpPreloadState = 'idle';
    stopAndClearVideo(video);
  }

  function pauseBackgroundPreloads(priorityTrigger) {
    backgroundPreloadsPaused = true;

    var candidates = preloadQueue.concat(Array.from(document.querySelectorAll('[data-evp-video-open]')));
    var deferred = [];
    var seen = new Set();
    preloadQueue = [];

    candidates.forEach(function (trigger) {
      if (!trigger || seen.has(trigger) || !document.contains(trigger)) return;
      seen.add(trigger);

      if (trigger === priorityTrigger) {
        if (trigger._evpPreloadState === 'queued') trigger._evpPreloadState = 'idle';
        return;
      }

      var state = trigger._evpPreloadState;
      if (state !== 'queued' && state !== 'loading' && state !== 'ready') return;

      if (trigger._evpPreparedVideo) cancelPreparedVideo(trigger);
      trigger._evpPreloadState = 'queued';
      deferred.push(trigger);
    });

    preloadQueue = deferred;
  }

  function resumeBackgroundPreloads() {
    backgroundPreloadsPaused = false;
    pumpPreloadQueue();
  }

  function trimPreloadPool(pool, videoToKeep) {
    if (!pool) return;

    var videos = Array.from(pool.querySelectorAll('video'));
    while (videos.length > maxPreparedVideos) {
      var video = videos.shift();
      if (!video || video === videoToKeep) continue;

      var trigger = video._evpTrigger;
      if (trigger && trigger._evpPreparedVideo === video) {
        cancelPreparedVideo(trigger);
      } else {
        stopAndClearVideo(video);
      }
    }
  }

  function recycleVideo(trigger, video) {
    if (!trigger || !video || video._evpFailed || !document.contains(trigger)) {
      stopAndClearVideo(video);
      return;
    }

    var root = trigger.closest('[data-event-portfolio]');
    var pool = getPreloadPool(root);
    if (!pool) {
      stopAndClearVideo(video);
      return;
    }

    try {
      video.pause();
      if (video.readyState > 0) video.currentTime = 0;
    } catch (error) {}

    video.controls = false;
    video.muted = true;
    video.defaultMuted = true;
    video.tabIndex = -1;
    video.setAttribute('aria-hidden', 'true');
    video.setAttribute('preload', 'auto');
    video._evpTrigger = trigger;
    trigger._evpPreparedVideo = video;
    trigger._evpPreloadState = 'ready';
    pool.appendChild(video);
    trimPreloadPool(pool, video);
  }

  function closeModal(options) {
    if (!activeModal) return;

    var modal = activeModal;
    var trigger = activeTrigger;
    var host = modal.querySelector('[data-evp-video-host]');
    var player = host && host.querySelector('.evp-player');
    var video = host && host.querySelector('video');
    var shouldRestoreFocus = !options || options.restoreFocus !== false;
    var shouldDiscardVideo = options && options.discardVideo === true;

    if (player && typeof player._evpDestroy === 'function') player._evpDestroy();

    if (video && trigger && !shouldDiscardVideo && !video._evpFailed) {
      recycleVideo(trigger, video);
      if (host) host.replaceChildren();
    } else {
      releaseVideoHost(host);
    }

    modal.hidden = true;
    modal.removeAttribute('data-video-ratio');
    activeModal = null;
    activeTrigger = null;
    unlockPageScroll();
    restoreModalOrigin(modal);

    if (!options || options.resumeHero !== false) resumeHeroPreviews();
    if (!options || options.resumePreloads !== false) resumeBackgroundPreloads();

    if (shouldRestoreFocus && trigger && document.contains(trigger)) {
      trigger.focus({ preventScroll: true });
    }
  }

  function reportVideoError(trigger, title, video) {
    var root = trigger.closest('[data-event-portfolio]');
    var status = root && root.querySelector('[data-evp-video-status]');

    if (video) video._evpFailed = true;
    if (trigger._evpPreparedVideo === video) trigger._evpPreparedVideo = null;
    trigger._evpPreloadState = 'error';

    closeModal({ restoreFocus: false, discardVideo: true });
    trigger.classList.add('is-video-error');
    trigger.setAttribute('aria-label', 'Retry video: ' + title);
    trigger.focus({ preventScroll: true });

    if (status) {
      status.textContent = 'The video could not load. Use Retry video to try again.';
    }
  }

  function pauseOtherVideos(currentVideo) {
    document.querySelectorAll('.event-portfolio video').forEach(function (video) {
      if (video !== currentVideo && !video.paused) {
        try {
          video.pause();
        } catch (error) {}
      }
    });
  }

  function formatTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';

    var minutes = Math.floor(seconds / 60);
    var remainingSeconds = Math.floor(seconds % 60);
    return minutes + ':' + String(remainingSeconds).padStart(2, '0');
  }

  function createPlayerIcon(name) {
    var namespace = 'http://www.w3.org/2000/svg';
    var icon = document.createElementNS(namespace, 'svg');
    var paths = {
      play: [{ d: 'M8 5.5v13L18.5 12z', fill: 'currentColor', stroke: 'none' }],
      pause: [{ d: 'M7 5h4v14H7zM13 5h4v14h-4z', fill: 'currentColor', stroke: 'none' }],
      volume: [
        { d: 'M4 9v6h4l5 4V5L8 9H4z', fill: 'currentColor', stroke: 'none' },
        { d: 'M16 9.2c.9.75 1.4 1.7 1.4 2.8s-.5 2.05-1.4 2.8M18.7 6.8c1.6 1.4 2.4 3.1 2.4 5.2s-.8 3.8-2.4 5.2' }
      ],
      muted: [
        { d: 'M4 9v6h4l5 4V5L8 9H4z', fill: 'currentColor', stroke: 'none' },
        { d: 'm17 9 4 4m0-4-4 4' }
      ],
      fullscreen: [{ d: 'M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5' }],
      fullscreenExit: [{ d: 'M3 8h5V3M21 8h-5V3M3 16h5v5M21 16h-5v5' }]
    };

    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '1.8');
    icon.setAttribute('stroke-linecap', 'round');
    icon.setAttribute('stroke-linejoin', 'round');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    icon.classList.add('evp-player__icon');

    (paths[name] || paths.play).forEach(function (definition) {
      var path = document.createElementNS(namespace, 'path');
      Object.keys(definition).forEach(function (attribute) {
        path.setAttribute(attribute, definition[attribute]);
      });
      icon.appendChild(path);
    });

    return icon;
  }

  function setButtonIcon(button, name) {
    button.replaceChildren(createPlayerIcon(name));
  }

  function createControlButton(className, label, iconName) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.setAttribute('aria-label', label);
    if (iconName) setButtonIcon(button, iconName);
    return button;
  }

  function buildPlayer(video, title) {
    var player = document.createElement('div');
    player.className = 'evp-player is-paused is-buffering';
    player.tabIndex = 0;
    player.setAttribute('aria-label', title + ' video player');

    var viewport = document.createElement('div');
    viewport.className = 'evp-player__viewport';

    var centerButton = createControlButton('evp-player__center', 'Play video', 'play');

    var loader = document.createElement('span');
    loader.className = 'evp-player__loader';
    loader.setAttribute('aria-hidden', 'true');

    var controls = document.createElement('div');
    controls.className = 'evp-player__controls';

    var playButton = createControlButton('evp-player__control evp-player__play-toggle', 'Play video', 'play');

    var progress = document.createElement('input');
    progress.className = 'evp-player__progress';
    progress.type = 'range';
    progress.min = '0';
    progress.max = '1000';
    progress.step = '1';
    progress.value = '0';
    progress.setAttribute('aria-label', 'Video progress');

    var soundButton = createControlButton('evp-player__control evp-player__sound', 'Mute video', 'volume');

    var volume = document.createElement('input');
    volume.className = 'evp-player__volume';
    volume.type = 'range';
    volume.min = '0';
    volume.max = '1';
    volume.step = '0.05';
    volume.value = String(video.volume || 1);
    volume.setAttribute('aria-label', 'Volume');

    var fullscreenButton = createControlButton('evp-player__control evp-player__fullscreen', 'Enter full screen', 'fullscreen');

    controls.append(playButton, progress, soundButton, volume, fullscreenButton);
    viewport.append(video, loader, centerButton);
    player.append(viewport, controls);

    video.controls = false;
    video.setAttribute('aria-label', title);
    video.removeAttribute('aria-hidden');
    video.removeAttribute('tabindex');

    var abortController = new AbortController();
    var signal = abortController.signal;
    var idleTimer = null;

    function showControls() {
      player.classList.remove('is-idle');
      player.classList.toggle('is-keyboard-active', lastInteractionWasKeyboard && player.matches(':focus-within'));
      window.clearTimeout(idleTimer);
      if (!video.paused) {
        idleTimer = window.setTimeout(function () {
          if (lastInteractionWasKeyboard && player.matches(':focus-within')) return;
          player.classList.remove('is-keyboard-active');
          player.classList.add('is-idle');
        }, 1200);
      }
    }

    function showPointerControls() {
      lastInteractionWasKeyboard = false;
      player.classList.remove('is-keyboard-active');
      showControls();
    }

    function updatePlayState() {
      var isPlaying = !video.paused && !video.ended;
      player.classList.toggle('is-playing', isPlaying);
      player.classList.toggle('is-paused', !isPlaying);
      playButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
      centerButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
      setButtonIcon(playButton, isPlaying ? 'pause' : 'play');
      setButtonIcon(centerButton, isPlaying ? 'pause' : 'play');
      showControls();
    }

    function updateTimeline() {
      var duration = Number.isFinite(video.duration) ? video.duration : 0;
      var currentTime = Number.isFinite(video.currentTime) ? video.currentTime : 0;
      var progressValue = duration ? Math.round((currentTime / duration) * 1000) : 0;
      var bufferedValue = 0;

      if (duration && video.buffered && video.buffered.length) {
        bufferedValue = Math.min(100, (video.buffered.end(video.buffered.length - 1) / duration) * 100);
      }

      progress.value = String(progressValue);
      progress.style.setProperty('--evp-progress', progressValue / 10 + '%');
      progress.style.setProperty('--evp-buffered', bufferedValue + '%');
      progress.setAttribute('aria-valuetext', formatTime(currentTime) + ' of ' + formatTime(duration));
    }

    function updateSoundState() {
      var isMuted = video.muted || video.volume === 0;
      setButtonIcon(soundButton, isMuted ? 'muted' : 'volume');
      soundButton.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
      volume.value = String(isMuted ? 0 : video.volume);
    }

    function togglePlayback() {
      if (video.paused || video.ended) {
        var promise = video.play();
        if (promise && typeof promise.catch === 'function') promise.catch(function () {});
      } else {
        video.pause();
      }
    }

    function toggleFullscreen() {
      if (document.fullscreenElement) {
        var exitPromise = document.exitFullscreen();
        if (exitPromise && typeof exitPromise.catch === 'function') exitPromise.catch(function () {});
        return;
      }

      if (player.requestFullscreen) {
        var fullscreenPromise = player.requestFullscreen();
        if (fullscreenPromise && typeof fullscreenPromise.catch === 'function') fullscreenPromise.catch(function () {});
      } else if (video.webkitEnterFullscreen) {
        video.controls = true;
        video.webkitEnterFullscreen();
      }
    }

    playButton.addEventListener('click', togglePlayback, { signal: signal });
    centerButton.addEventListener('click', togglePlayback, { signal: signal });
    video.addEventListener('click', togglePlayback, { signal: signal });
    video.addEventListener('dblclick', toggleFullscreen, { signal: signal });
    video.addEventListener('play', updatePlayState, { signal: signal });
    video.addEventListener('pause', updatePlayState, { signal: signal });
    video.addEventListener('ended', updatePlayState, { signal: signal });
    video.addEventListener('timeupdate', updateTimeline, { signal: signal });
    video.addEventListener('durationchange', updateTimeline, { signal: signal });
    video.addEventListener('progress', updateTimeline, { signal: signal });
    video.addEventListener(
      'waiting',
      function () {
        player.classList.add('is-buffering');
      },
      { signal: signal }
    );
    video.addEventListener(
      'playing',
      function () {
        player.classList.remove('is-buffering');
        updatePlayState();
      },
      { signal: signal }
    );
    video.addEventListener(
      'canplay',
      function () {
        player.classList.remove('is-buffering');
      },
      { signal: signal }
    );
    video.addEventListener('volumechange', updateSoundState, { signal: signal });
    video.addEventListener(
      'webkitendfullscreen',
      function () {
        video.controls = false;
      },
      { signal: signal }
    );

    progress.addEventListener(
      'input',
      function () {
        if (Number.isFinite(video.duration)) {
          video.currentTime = (Number(progress.value) / 1000) * video.duration;
          updateTimeline();
        }
      },
      { signal: signal }
    );

    soundButton.addEventListener(
      'click',
      function () {
        video.muted = !video.muted;
        if (!video.muted && video.volume === 0) video.volume = 0.8;
      },
      { signal: signal }
    );

    volume.addEventListener(
      'input',
      function () {
        video.volume = Number(volume.value);
        video.muted = video.volume === 0;
      },
      { signal: signal }
    );

    fullscreenButton.addEventListener('click', toggleFullscreen, { signal: signal });
    document.addEventListener(
      'fullscreenchange',
      function () {
        var isFullscreen = document.fullscreenElement === player;
        setButtonIcon(fullscreenButton, isFullscreen ? 'fullscreenExit' : 'fullscreen');
        fullscreenButton.setAttribute('aria-label', isFullscreen ? 'Exit full screen' : 'Enter full screen');
      },
      { signal: signal }
    );

    player.addEventListener('pointermove', showPointerControls, { signal: signal });
    player.addEventListener('pointerdown', showPointerControls, { signal: signal });
    player.addEventListener(
      'keydown',
      function () {
        lastInteractionWasKeyboard = true;
        player.classList.add('is-keyboard-active');
        showControls();
      },
      { signal: signal }
    );
    player.addEventListener(
      'focusin',
      function () {
        player.classList.toggle('is-keyboard-active', lastInteractionWasKeyboard);
        showControls();
      },
      { signal: signal }
    );

    player._evpTogglePlayback = togglePlayback;
    player._evpToggleFullscreen = toggleFullscreen;
    player._evpDestroy = function () {
      window.clearTimeout(idleTimer);
      abortController.abort();
    };

    updatePlayState();
    updateTimeline();
    updateSoundState();
    return player;
  }

  function claimVideo(trigger, template) {
    var video = trigger._evpPreparedVideo;

    if (video && !video._evpFailed) {
      trigger._evpPreparedVideo = null;
      trigger._evpPreloadState = 'consumed';
      return video;
    }

    trigger._evpPreloadState = 'consumed';
    var fragment = template.content.cloneNode(true);
    video = fragment.querySelector('video');
    if (!video) return null;

    video._evpTrigger = trigger;
    return video;
  }

  function openVideo(trigger) {
    var root = trigger.closest('[data-event-portfolio]');
    if (!root) return;

    var modal = root.querySelector('[data-evp-video-modal]');
    var template = document.getElementById(trigger.dataset.templateId);
    if (!modal || !template) return;

    if (activeModal) closeModal({ restoreFocus: false, resumePreloads: false });

    var host = modal.querySelector('[data-evp-video-host]');
    var title = trigger.dataset.videoTitle || 'VTC WORKS video';
    var requestedRatio = trigger.dataset.videoRatio;
    var ratio = requestedRatio === 'portrait' || requestedRatio === 'square' ? requestedRatio : 'landscape';
    var titleElement = modal.querySelector('[data-evp-video-modal-title]');
    var closeButton = modal.querySelector('.evp-video-modal__close');

    pauseBackgroundPreloads(trigger);
    suspendHeroPreviews();
    pauseOtherVideos(null);
    releaseVideoHost(host);

    var video = claimVideo(trigger, template);
    if (!video) {
      resumeBackgroundPreloads();
      return;
    }

    video._evpFailed = false;
    video.playsInline = true;
    video.preload = 'auto';
    video.muted = false;
    video.defaultMuted = false;
    video.removeAttribute('muted');
    video.setAttribute('playsinline', '');
    video.setAttribute('preload', 'auto');

    var player = buildPlayer(video, title);
    host.replaceChildren(player);

    trigger.classList.remove('is-video-error');
    trigger.setAttribute('aria-label', 'Play ' + title);
    titleElement.textContent = title;
    mountModalInViewport(modal);
    modal.dataset.videoRatio = ratio;
    modal.hidden = false;
    activeModal = modal;
    activeTrigger = trigger;
    lockPageScroll();

    var mediaFailureReported = false;
    var handleMediaFailure = function () {
      if (mediaFailureReported) return;
      if (activeModal !== modal || activeTrigger !== trigger || !video.isConnected) return;
      mediaFailureReported = true;
      reportVideoError(trigger, title, video);
    };

    // A Shopify video can contain several source formats. One source may fail
    // while the browser successfully falls back to another, so only the video
    // element's final error should close the modal.
    video.addEventListener('error', handleMediaFailure, { once: true });

    var playPromise;
    try {
      playPromise = video.play();
    } catch (error) {
      handleMediaFailure();
      return;
    }

    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function (error) {
        if (error && (error.name === 'NotAllowedError' || error.name === 'AbortError')) {
          player.classList.remove('is-buffering');
          return;
        }
        handleMediaFailure();
      });
    }

    if (closeButton) closeButton.focus({ preventScroll: true });
  }

  function connectionIsConstrained() {
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return false;
    return connection.saveData || connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
  }

  function finishPreloadSlot() {
    activePreloads = Math.max(0, activePreloads - 1);
    pumpPreloadQueue();
  }

  function prepareVideo(trigger) {
    var root = trigger.closest('[data-event-portfolio]');
    var template = document.getElementById(trigger.dataset.templateId);
    var pool = getPreloadPool(root);
    if (!root || !template || !pool || !document.contains(trigger)) {
      trigger._evpPreloadState = 'idle';
      finishPreloadSlot();
      return;
    }

    var fragment = template.content.cloneNode(true);
    var video = fragment.querySelector('video');
    if (!video) {
      trigger._evpPreloadState = 'idle';
      finishPreloadSlot();
      return;
    }

    trigger._evpPreloadState = 'loading';
    trigger._evpPreparedVideo = video;
    video._evpTrigger = trigger;
    video._evpFailed = false;
    video.controls = false;
    video.preload = 'auto';
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.tabIndex = -1;
    video.setAttribute('aria-hidden', 'true');
    video.setAttribute('playsinline', '');
    video.setAttribute('preload', 'auto');
    pool.appendChild(video);
    trimPreloadPool(pool, video);

    var slotReleased = false;
    var timeout = null;
    var detachPreloadListeners = function () {
      video.removeEventListener('loadeddata', releaseSlot);
      video.removeEventListener('canplay', releaseSlot);
      video.removeEventListener('error', handleFailure);
      video._evpCancelPreload = null;
    };
    var releaseSlot = function () {
      if (slotReleased) return;
      slotReleased = true;
      window.clearTimeout(timeout);
      detachPreloadListeners();
      if (trigger._evpPreloadState === 'loading') trigger._evpPreloadState = 'ready';
      finishPreloadSlot();
    };

    var handleFailure = function () {
      if (slotReleased) return;
      slotReleased = true;
      window.clearTimeout(timeout);
      detachPreloadListeners();
      if (trigger._evpPreparedVideo === video) trigger._evpPreparedVideo = null;
      trigger._evpPreloadState = 'idle';
      stopAndClearVideo(video);
      finishPreloadSlot();
    };

    video._evpCancelPreload = function () {
      if (slotReleased) return;
      slotReleased = true;
      window.clearTimeout(timeout);
      detachPreloadListeners();
      if (trigger._evpPreparedVideo === video) trigger._evpPreparedVideo = null;
      trigger._evpPreloadState = 'idle';
      stopAndClearVideo(video);
      finishPreloadSlot();
    };

    video.addEventListener('loadeddata', releaseSlot, { once: true });
    video.addEventListener('canplay', releaseSlot, { once: true });
    video.addEventListener('error', handleFailure, { once: true });

    timeout = window.setTimeout(releaseSlot, 8000);
    try {
      video.load();
    } catch (error) {
      handleFailure();
    }
  }

  function pumpPreloadQueue() {
    if (backgroundPreloadsPaused) return;

    var concurrencyLimit = window.matchMedia('(max-width: 749px)').matches ? 1 : maxConcurrentPreloads;

    while (activePreloads < concurrencyLimit && preloadQueue.length) {
      var trigger = preloadQueue.shift();
      if (!trigger || !document.contains(trigger) || trigger._evpPreloadState !== 'queued') continue;
      activePreloads += 1;
      prepareVideo(trigger);
    }
  }

  function enqueueVideoPreload(trigger, priority) {
    if (!trigger || backgroundPreloadsPaused || connectionIsConstrained()) return;

    var state = trigger._evpPreloadState;
    if (state === 'queued' || state === 'loading' || state === 'ready' || state === 'consumed') return;

    trigger._evpPreloadState = 'queued';
    if (priority) preloadQueue.unshift(trigger);
    else preloadQueue.push(trigger);
    pumpPreloadQueue();
  }

  function initVideoPreloading(scope) {
    if (connectionIsConstrained()) return;

    var triggers = (scope || document).querySelectorAll('[data-evp-video-open]');
    if (!triggers.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.from(triggers)
        .slice(0, 3)
        .forEach(function (trigger) {
          enqueueVideoPreload(trigger, false);
        });
      return;
    }

    if (!preloadObserver) {
      preloadObserver = new IntersectionObserver(
        function (entries, observer) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            entry.target.dataset.evpPreloadObserved = 'done';
            enqueueVideoPreload(entry.target, false);
          });
        },
        { rootMargin: '80% 0px 100% 0px', threshold: 0.01 }
      );
    }

    triggers.forEach(function (trigger) {
      if (trigger.dataset.evpPreloadObserved) return;
      trigger.dataset.evpPreloadObserved = 'true';
      preloadObserver.observe(trigger);
    });
  }

  function destroyVideoPreloading(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-evp-video-open]').forEach(function (trigger) {
      if (preloadObserver) preloadObserver.unobserve(trigger);
      if (trigger._evpPreparedVideo) cancelPreparedVideo(trigger);
      trigger._evpPreparedVideo = null;
      trigger._evpPreloadState = 'idle';
    });

    preloadQueue = preloadQueue.filter(function (trigger) {
      return !root.contains(trigger);
    });
  }

  function canLoadHeroPreview() {
    return (
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      !connectionIsConstrained() &&
      !(navigator.deviceMemory && navigator.deviceMemory <= 2)
    );
  }

  function startHeroPreview(root, onSettled) {
    var hasSettled = false;
    var settle = function () {
      if (hasSettled) return;
      hasSettled = true;
      if (typeof onSettled === 'function') onSettled();
    };

    if (!root || root.dataset.evpHeroReady === 'true' || !canLoadHeroPreview()) {
      settle();
      return;
    }

    var template = root.querySelector('[data-evp-hero-template]');
    var media = root.querySelector('.evp-hero__media');
    if (!template || !media || document.body.classList.contains('evp-video-open')) {
      settle();
      return;
    }

    root.dataset.evpHeroReady = 'true';
    media.appendChild(template.content.cloneNode(true));

    var video = media.querySelector('.evp-hero__preview-video');
    if (!video) {
      settle();
      return;
    }

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('preload', 'auto');
    video.setAttribute('fetchpriority', 'high');
    video.querySelectorAll('source').forEach(function (source) {
      source.setAttribute('fetchpriority', 'high');
    });

    var priorityTimeout = window.setTimeout(settle, 8000);
    var finishPriorityLoad = function () {
      window.clearTimeout(priorityTimeout);
      settle();
    };

    video.addEventListener(
      'playing',
      function () {
        media.classList.add('is-previewing');
        finishPriorityLoad();
      },
      { once: true }
    );

    video.addEventListener(
      'error',
      function () {
        media.classList.remove('is-previewing');
        video.remove();
        finishPriorityLoad();
      },
      { once: true }
    );

    video.load();
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {
        media.classList.remove('is-previewing');
        video.remove();
        finishPriorityLoad();
      });
    }
  }

  function startMediaPipeline(scope) {
    var roots = (scope || document).querySelectorAll('[data-event-portfolio]');
    roots.forEach(function (root) {
      startHeroPreview(root, function () {
        initVideoPreloading(root);
      });
    });
  }

  function layoutMasonryGrid(grid) {
    if (!grid || !grid.isConnected) return;

    var projects = Array.from(grid.querySelectorAll('.evp-project'));
    if (!projects.length) return;

    if (window.matchMedia('(max-width: 749px)').matches) {
      grid.classList.remove('is-masonry-ready');
      projects.forEach(function (project) {
        project.style.gridRowEnd = '';
      });
      return;
    }

    grid.classList.add('is-masonry-ready');

    var gridStyles = window.getComputedStyle(grid);
    var rowHeight = parseFloat(gridStyles.gridAutoRows);
    var rowGap = parseFloat(gridStyles.rowGap);
    if (!rowHeight || Number.isNaN(rowHeight) || Number.isNaN(rowGap)) return;

    projects.forEach(function (project) {
      var media = project.querySelector('.evp-project__media');
      if (!media) return;

      var mediaHeight = media.getBoundingClientRect().height;
      var rowSpan = Math.max(1, Math.ceil((mediaHeight + rowGap) / (rowHeight + rowGap)));
      project.style.gridRowEnd = 'span ' + rowSpan;
    });
  }

  function scheduleMasonryLayout(grid) {
    if (!grid || grid._evpMasonryFrame) return;

    grid._evpMasonryFrame = window.requestAnimationFrame(function () {
      grid._evpMasonryFrame = null;
      layoutMasonryGrid(grid);
    });
  }

  function initMasonry(scope) {
    var grids = (scope || document).querySelectorAll('[data-evp-masonry]');

    grids.forEach(function (grid) {
      if (grid.dataset.evpMasonryReady === 'true') {
        scheduleMasonryLayout(grid);
        return;
      }

      grid.dataset.evpMasonryReady = 'true';
      var lastWidth = 0;

      if ('ResizeObserver' in window) {
        grid._evpMasonryObserver = new ResizeObserver(function (entries) {
          var nextWidth = entries[0] ? entries[0].contentRect.width : grid.clientWidth;
          if (Math.abs(nextWidth - lastWidth) < 1) return;
          lastWidth = nextWidth;
          scheduleMasonryLayout(grid);
        });
        grid._evpMasonryObserver.observe(grid);
      }

      grid.querySelectorAll('img').forEach(function (image) {
        if (!image.complete) {
          image.addEventListener(
            'load',
            function () {
              scheduleMasonryLayout(grid);
            },
            { once: true }
          );
        }
      });

      scheduleMasonryLayout(grid);
    });
  }

  function destroyMasonry(scope) {
    (scope || document).querySelectorAll('[data-evp-masonry]').forEach(function (grid) {
      if (grid._evpMasonryObserver) grid._evpMasonryObserver.disconnect();
      if (grid._evpMasonryFrame) window.cancelAnimationFrame(grid._evpMasonryFrame);
    });
  }

  document.addEventListener('pointerover', function (event) {
    var trigger = event.target.closest('[data-evp-video-open]');
    if (trigger) enqueueVideoPreload(trigger, true);
  });

  document.addEventListener('focusin', function (event) {
    var trigger = event.target.closest('[data-evp-video-open]');
    if (trigger) enqueueVideoPreload(trigger, true);
  });

  document.addEventListener(
    'touchstart',
    function (event) {
      var trigger = event.target.closest('[data-evp-video-open]');
      if (trigger) enqueueVideoPreload(trigger, true);
    },
    { passive: true }
  );

  document.addEventListener('click', function (event) {
    var openButton = event.target.closest('[data-evp-video-open]');
    if (openButton) {
      event.preventDefault();
      openVideo(openButton);
      return;
    }

    var closeButton = event.target.closest('[data-evp-video-close]');
    if (closeButton && activeModal && activeModal.contains(closeButton)) {
      event.preventDefault();
      closeModal();
      return;
    }

    if (
      activeModal &&
      activeModal.contains(event.target) &&
      !event.target.closest('.evp-player') &&
      !event.target.closest('.evp-video-modal__close')
    ) {
      event.preventDefault();
      closeModal();
    }
  });

  document.addEventListener(
    'play',
    function (event) {
      if (event.target instanceof HTMLVideoElement && event.target.closest('.event-portfolio')) {
        if (activeModal && event.target.classList.contains('evp-hero__preview-video')) {
          if (!suspendedHeroVideos.includes(event.target)) suspendedHeroVideos.push(event.target);
          try {
            event.target.pause();
          } catch (error) {}
          return;
        }
        pauseOtherVideos(event.target);
      }
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    lastInteractionWasKeyboard = true;
    if (!activeModal) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal();
      return;
    }

    var player = activeModal.querySelector('.evp-player');
    var video = player && player.querySelector('video');
    var activeTag = document.activeElement ? document.activeElement.tagName : '';
    var isFormControl = activeTag === 'INPUT' || activeTag === 'BUTTON' || activeTag === 'A';

    if (player && video && !isFormControl) {
      if (event.key === ' ' || event.key.toLowerCase() === 'k') {
        event.preventDefault();
        player._evpTogglePlayback();
        return;
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        var direction = event.key === 'ArrowLeft' ? -5 : 5;
        video.currentTime = Math.max(0, Math.min(video.duration || 0, video.currentTime + direction));
        return;
      }
      if (event.key.toLowerCase() === 'm') {
        event.preventDefault();
        video.muted = !video.muted;
        return;
      }
      if (event.key.toLowerCase() === 'f') {
        event.preventDefault();
        player._evpToggleFullscreen();
        return;
      }
    }

    if (event.key === 'Tab') {
      var focusable = getFocusableElements(activeModal);
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var modalOrigin = activeModal && activeModal._evpOriginParent;
    if (activeModal && (event.target.contains(activeModal) || (modalOrigin && event.target.contains(modalOrigin)))) {
      closeModal({ restoreFocus: false, discardVideo: true, resumePreloads: false });
    }
    destroyVideoPreloading(event.target);
    destroyMasonry(event.target);
  });

  document.addEventListener('shopify:section:load', function (event) {
    startMediaPipeline(event.target);
    initMasonry(event.target);
  });

  window.addEventListener('pagehide', function () {
    closeModal({ restoreFocus: false, discardVideo: true, resumeHero: false, resumePreloads: false });
    destroyVideoPreloading(document);
  });

  startMediaPipeline(document);

  if (document.readyState === 'complete') {
    initMasonry(document);
  } else {
    window.addEventListener(
      'load',
      function () {
        initMasonry(document);
      },
      { once: true }
    );
  }
})();
